/**
 * Validates media requirements that can be inspected in the browser.
 * Meta converts accepted image color profiles; the browser cannot verify profiles on unchanged images.
 * Browser metadata does not expose every encoded video property required by the platform.
 * @see https://developers.facebook.com/documentation/instagram-platform/instagram-graph-api/reference/ig-user/media
 */
import { TFunction } from 'i18next';
import { ImageColorSpace } from './imageConversion';

interface AspectRatioRange {
  minimum: number;
  maximum: number;
}

interface VideoProperties {
  duration: number;
  width: number;
  height: number;
}

const STORY_IMAGE_RECOMMENDED_ASPECT_RATIO = 9 / 16;
const FEED_IMAGE_ASPECT_RATIO_RANGE: AspectRatioRange = { minimum: 0.8, maximum: 1.91 };
const RECOMMENDED_VIDEO_ASPECT_RATIO = 9 / 16;
const MINIMUM_FEED_IMAGE_WIDTH = 320;
const MAXIMUM_FEED_IMAGE_WIDTH = 1440;
const STORY_VIDEO_ASPECT_RATIO_RANGE: AspectRatioRange = { minimum: 0.1, maximum: 10 };
const REELS_VIDEO_ASPECT_RATIO_RANGE: AspectRatioRange = { minimum: 0.01, maximum: 10 };
const MAX_VIDEO_WIDTH = 1920;
const SUPPORTED_VIDEO_MIME_TYPES = new Set(['video/mp4', 'video/quicktime']);
const GENERIC_VIDEO_MIME_TYPES = new Set(['', 'application/octet-stream']);
const SUPPORTED_VIDEO_EXTENSIONS = new Set(['mp4', 'mov']);

const isAspectRatioWithinRange = (aspectRatio: number, range: AspectRatioRange): boolean =>
  aspectRatio >= range.minimum && aspectRatio <= range.maximum;

const hasSupportedVideoExtension = (file: File): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return SUPPORTED_VIDEO_EXTENSIONS.has(extension);
};

const isVideoFile = (file: File): boolean => {
  const fileType = file.type.toLowerCase();
  return fileType.startsWith('video/') || (
    GENERIC_VIDEO_MIME_TYPES.has(fileType) && hasSupportedVideoExtension(file)
  );
};

const isSupportedVideoFormat = (file: File): boolean => {
  const fileType = file.type.toLowerCase();
  return SUPPORTED_VIDEO_MIME_TYPES.has(fileType) || (
    GENERIC_VIDEO_MIME_TYPES.has(fileType) && hasSupportedVideoExtension(file)
  );
};

const getReportedFormat = (file: File): string => {
  const extension = file.name.split('.').pop();
  return file.type || (extension && extension !== file.name ? extension.toUpperCase() : 'unknown');
};

const getFileDetails = (file: File) => ({
  format: getReportedFormat(file),
  size: (file.size / 1024 / 1024).toFixed(2),
  sizeBytes: file.size.toString(),
});

const hasJpegSignature = async (file: File): Promise<boolean> => {
  const signature = new Uint8Array(await file.slice(0, 3).arrayBuffer());
  return signature[0] === 0xff && signature[1] === 0xd8 && signature[2] === 0xff;
};

const hasValidVideoProperties = (properties: VideoProperties): boolean =>
  Number.isFinite(properties.duration) &&
  Number.isFinite(properties.width) &&
  Number.isFinite(properties.height) &&
  properties.width > 0 &&
  properties.height > 0;

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  requiresImageEdit?: boolean;
}

interface ImageValidationOptions {
  t: TFunction;
  colorSpace?: ImageColorSpace;
}

export interface MediaFile {
  file: File;
  type: 'image' | 'video';
}

/**
 * Validates JPEG images for Feed and Stories against browser-inspectable requirements.
 * - Enforces a JPEG signature and a maximum size of 8 MiB (Meta specifies 8 MB).
 * - Feed: aspect ratio 0.8:1–1.91:1 and width 320–1,440 px.
 * - Stories: 9:16 is recommended, not required.
 * The editor can resize Feed images outside its ratio or width limits. Edited images are
 * encoded as sRGB; the browser cannot inspect the embedded color profile of unchanged JPEGs.
 * @see https://developers.facebook.com/documentation/instagram-platform/instagram-graph-api/reference/ig-user/media#image-specifications
 * @see https://developers.facebook.com/documentation/instagram-platform/instagram-graph-api/reference/ig-user/media#story-image-specifications
 */
export const validateImage = async (
  file: File,
  context: 'stories' | 'feed',
  { t, colorSpace = 'unknown' }: ImageValidationOptions,
): Promise<ValidationResult> => {
  const result: ValidationResult = {
    isValid: true,
    errors: [],
    warnings: file.type.toLowerCase() === 'image/jpeg' && colorSpace === 'unknown'
      ? [t('validation.imageColorSpaceWarning')]
      : [],
    requiresImageEdit: false,
  };

  if (file.type.toLowerCase() !== 'image/jpeg' || !(await hasJpegSignature(file))) {
    result.errors.push(t('validation.imageFormatError', getFileDetails(file)));
    result.isValid = false;
    result.requiresImageEdit = true;
  }

  const maxSizeInBytes = 8 * 1024 * 1024;
  if (file.size > maxSizeInBytes) {
    result.errors.push(t('validation.imageSizeError', getFileDetails(file)));
    result.isValid = false;
    result.requiresImageEdit = true;
  }

  try {
    const dimensions = await getImageDimensions(file, t);
    if (
      !Number.isFinite(dimensions.width) ||
      !Number.isFinite(dimensions.height) ||
      dimensions.width <= 0 ||
      dimensions.height <= 0
    ) {
      throw new Error('Invalid image dimensions');
    }

    const aspectRatio = dimensions.width / dimensions.height;

    if (context === 'stories' && aspectRatio !== STORY_IMAGE_RECOMMENDED_ASPECT_RATIO) {
      result.warnings.push(t('validation.imageAspectRatioWarning', { ratio: aspectRatio.toString() }));
    }
    if (context === 'feed') {
      if (!isAspectRatioWithinRange(aspectRatio, FEED_IMAGE_ASPECT_RATIO_RANGE)) {
        result.errors.push(t('validation.imageAspectRatioError', { ratio: aspectRatio.toString() }));
        result.isValid = false;
        result.requiresImageEdit = true;
      }
      if (dimensions.width < MINIMUM_FEED_IMAGE_WIDTH || dimensions.width > MAXIMUM_FEED_IMAGE_WIDTH) {
        result.errors.push(t('validation.feedImageWidthError', { width: dimensions.width }));
        result.isValid = false;
        result.requiresImageEdit = true;
      }
    }

  } catch (error) {
    console.error('Erro ao analisar dimensões da imagem:', error);
    result.errors.push(t('validation.imageDimensionsError', getFileDetails(file)));
    result.isValid = false;
  }

  return result;
};

/**
 * Enforces browser-inspectable Story video requirements: reported MOV/MP4 format,
 * a maximum size of 100 MiB (104,857,600 bytes; Meta specifies 100 MB), duration of
 * 3–60 seconds, width up to 1,920 px, and aspect ratio of 0.1:1–10:1. A 9:16 ratio
 * is recommended and reported as a warning.
 * Format checks use the reported MIME type, falling back to an .mp4/.mov extension
 * only when the MIME type is blank or generic; they do not inspect the container.
 * Browser metadata cannot verify edit lists, moov-atom position, codecs, frame rate,
 * scan type, GOP, chroma subsampling, audio properties, or bitrates; these are not enforced.
 * @see https://developers.facebook.com/documentation/instagram-platform/instagram-graph-api/reference/ig-user/media#story-video-specifications
 */
export const validateStoryVideo = async (file: File, t: TFunction): Promise<ValidationResult> => {
  const result: ValidationResult = {
    isValid: true,
    errors: [],
    warnings: []
  };

  if (!isSupportedVideoFormat(file)) {
    result.errors.push(t('validation.videoFormatError', getFileDetails(file)));
    result.isValid = false;
  }

  const maxSizeInBytes = 100 * 1024 * 1024; // 100MB
  if (file.size > maxSizeInBytes) {
    result.errors.push(t('validation.videoStoriesSizeError', getFileDetails(file)));
    result.isValid = false;
  }
  try {
    const videoProperties = await getVideoProperties(file, t);
    result.warnings.push(t('validation.videoPropertiesWarning'));
    if (!hasValidVideoProperties(videoProperties)) {
      result.errors.push(t('validation.videoLoadError', getFileDetails(file)));
      result.isValid = false;
      return result;
    }

    if (videoProperties.duration < 3) {
      result.errors.push(t('validation.videoDurationMinError', { duration: videoProperties.duration.toString() }));
      result.isValid = false;
    }
    
    if (videoProperties.duration > 60) {
      result.errors.push(t('validation.videoDurationStoriesMaxError', { duration: videoProperties.duration.toString() }));
      result.isValid = false;
    }

    if (videoProperties.width > MAX_VIDEO_WIDTH) {
      result.errors.push(t('validation.videoWidthError', { width: videoProperties.width }));
      result.isValid = false;
    }

    const aspectRatio = videoProperties.width / videoProperties.height;
    if (!isAspectRatioWithinRange(aspectRatio, STORY_VIDEO_ASPECT_RATIO_RANGE)) {
      result.errors.push(t('validation.videoAspectRatioError', { ratio: aspectRatio.toString() }));
      result.isValid = false;
    } else if (Math.abs(aspectRatio - RECOMMENDED_VIDEO_ASPECT_RATIO) > 0.01) {
      result.warnings.push(t('validation.videoAspectRatioRecommended', { ratio: aspectRatio.toString() }));
    }

  } catch (error) {
    console.error('Erro ao analisar propriedades do vídeo:', error);
    result.errors.push(t('validation.videoLoadError', getFileDetails(file)));
    result.isValid = false;
    result.warnings.push(t('validation.videoPropertiesWarning'));
  }

  return result;
};

/**
 * Main function to validate Stories media
 */
export const validateStoryMedia = async (file: File, t: TFunction): Promise<ValidationResult> => {
  if (file.type.startsWith('image/')) {
    return await validateImage(file, 'stories', { t });
  } else if (isVideoFile(file)) {
    return await validateStoryVideo(file, t);
  } else {
    return {
      isValid: false,
      errors: [t('validation.storiesUnsupportedType', getFileDetails(file))],
      warnings: []
    };
  }
};

/**
 * Enforces browser-inspectable Reels video requirements: reported MOV/MP4 format,
 * a maximum size of 300 MiB (314,572,800 bytes; Meta specifies 300 MB), duration of
 * 3 seconds–15 minutes, width up to 1,920 px, and aspect ratio of 0.01:1–10:1. A 9:16
 * ratio is recommended and reported as a warning.
 * Format checks use the reported MIME type, falling back to an .mp4/.mov extension
 * only when the MIME type is blank or generic; they do not inspect the container.
 * Browser metadata cannot verify edit lists, moov-atom position, codecs, frame rate,
 * scan type, GOP, chroma subsampling, audio properties, or bitrates; these are not enforced.
 * Reels covers are handled separately by the cover image editor, which exports JPEG,
 * limits the final file to 8 MiB, and enforces a 9:16 crop.
 * @see https://developers.facebook.com/documentation/instagram-platform/instagram-graph-api/reference/ig-user/media#reels-specs
 */
export const validateReelsVideo = async (file: File, t: TFunction): Promise<ValidationResult> => {
  const result: ValidationResult = {
    isValid: true,
    errors: [],
    warnings: []
  };

  if (!isSupportedVideoFormat(file)) {
    result.errors.push(t('validation.videoFormatError', getFileDetails(file)));
    result.isValid = false;
  }

  const maxSizeInBytes = 300 * 1024 * 1024;
  if (file.size > maxSizeInBytes) {
    result.errors.push(t('validation.videoReelsSizeError', getFileDetails(file)));
    result.isValid = false;
  }

  try {
    const videoProperties = await getVideoProperties(file, t);
    result.warnings.push(t('validation.videoPropertiesWarning'));
    if (!hasValidVideoProperties(videoProperties)) {
      result.errors.push(t('validation.videoLoadError', getFileDetails(file)));
      result.isValid = false;
      return result;
    }

    if (videoProperties.duration < 3) {
      result.errors.push(t('validation.videoDurationMinError', { duration: videoProperties.duration.toString() }));
      result.isValid = false;
    }
    
    if (videoProperties.duration > 900) {
      result.errors.push(t('validation.videoDurationReelsMaxError', { duration: videoProperties.duration.toString() }));
      result.isValid = false;
    }

    if (videoProperties.width > MAX_VIDEO_WIDTH) {
      result.errors.push(t('validation.videoWidthError', { width: videoProperties.width }));
      result.isValid = false;
    }

    const aspectRatio = videoProperties.width / videoProperties.height;
    if (!isAspectRatioWithinRange(aspectRatio, REELS_VIDEO_ASPECT_RATIO_RANGE)) {
      result.errors.push(t('validation.videoReelsAspectRatioError', { ratio: aspectRatio.toString() }));
      result.isValid = false;
    } else if (Math.abs(aspectRatio - RECOMMENDED_VIDEO_ASPECT_RATIO) > 0.01) {
      result.warnings.push(t('validation.videoAspectRatioRecommended', { ratio: aspectRatio.toString() }));
    }

  } catch (error) {
    console.error('Erro ao analisar propriedades do vídeo:', error);
    result.errors.push(t('validation.videoLoadError', getFileDetails(file)));
    result.isValid = false;
    result.warnings.push(t('validation.videoPropertiesWarning'));
  }

  return result;
};

/**
 * Main function to validate Reels media
 * Reels accepts only MOV or MP4 videos
 */
export const validateReelsMedia = async (file: File, t: TFunction): Promise<ValidationResult> => {
  if (isVideoFile(file)) {
    return await validateReelsVideo(file, t);
  } else {
    return {
      isValid: false,
      errors: [t('validation.reelsVideoOnly', getFileDetails(file))],
      warnings: []
    };
  }
};

/**
 * Validates caption for Reels
 * Specifications:
 * - Maximum 2200 characters
 * - Maximum 30 hashtags (#)
 * - Maximum 20 mentions (@)
 */
export const validateReelsCaption = (caption: string, t: TFunction): ValidationResult => {
  const result: ValidationResult = {
    isValid: true,
    errors: [],
    warnings: []
  };

  const hashtags = caption.match(/#\w+/g) || [];
  if (hashtags.length > 30) {
    result.errors.push(t('validation.hashtagsError', { count: hashtags.length }));
    result.isValid = false;
  }

  const mentions = caption.match(/@\w+/g) || [];
  if (mentions.length > 20) {
    result.errors.push(t('validation.mentionsError', { count: mentions.length }));
    result.isValid = false;
  }

  return result;
};

/**
 * Main function to validate Feed media
 * Feed accepts only JPEG images
 */
export const validateFeedMedia = async (file: File, t: TFunction): Promise<ValidationResult> => {
  if (file.type.startsWith('image/')) {
    return await validateImage(file, 'feed', { t });
  } else {
    return {
      isValid: false,
      errors: [t('validation.feedImageOnly', getFileDetails(file))],
      warnings: []
    };
  }
};

/**
 * Utility to get image dimensions
 */
const getImageDimensions = (file: File, t: TFunction): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(t('validation.imageLoadError')));
    };

    img.src = url;
  });
};

/**
 * Utility to get video properties
 * Note: Some advanced properties (like codec, bitrate) may not be available via browser
 */
const getVideoPropertiesFromObjectUrl = (
  video: HTMLVideoElement,
  file: File,
  t: TFunction,
): Promise<VideoProperties> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight
      });
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      console.error('Erro ao carregar propriedades do vídeo:', {
        fileType: file.type,
        fileSizeBytes: file.size,
        mediaError: video.error ?? null,
      });
      reject(new Error(t('validation.videoLoadError')));
    };

    video.srcObject = null;
    video.src = url;
    video.load();
  });

const getVideoProperties = (file: File, t: TFunction): Promise<VideoProperties> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const useObjectUrlFallback = () => {
      getVideoPropertiesFromObjectUrl(video, file, t).then(resolve, reject);
    };

    video.onloadedmetadata = () => {
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight
      });
    };

    video.onerror = useObjectUrlFallback;

    try {
      video.srcObject = file;
      video.load();
    } catch {
      useObjectUrlFallback();
    }
  });
};